import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, User as UserIcon, GraduationCap } from "lucide-react";
import { Show, useUser, useClerk } from "@clerk/react";
import {
  useGetMyTeacherProfile,
  getGetMyTeacherProfileQueryKey,
} from "@workspace/api-client-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Teacher accounts are invite-only — an admin adds a row to the
// teacher_allowlist table before that email can claim a teacher profile.
// GET /teacher/me returns 404 for anyone without an allowlist or teachers
// row, so we use that signal to hide the teacher portal entry points from
// regular students. The hook is only mounted inside signed-in surfaces so
// signed-out visitors never make the request.
function useIsTeacher(): { isLoading: boolean; isTeacher: boolean } {
  const { isLoaded, isSignedIn } = useUser();
  const query = useGetMyTeacherProfile({
    query: {
      retry: false,
      enabled: isLoaded && !!isSignedIn,
      queryKey: getGetMyTeacherProfileQueryKey(),
    },
  });
  if (!isLoaded || !isSignedIn) return { isLoading: false, isTeacher: false };
  if (query.isLoading) return { isLoading: true, isTeacher: false };
  return { isLoading: false, isTeacher: !query.isError && !!query.data };
}

function UserMenu() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [, setLocation] = useLocation();
  const { isTeacher } = useIsTeacher();

  const initial =
    user?.firstName?.charAt(0)?.toUpperCase() ||
    user?.primaryEmailAddress?.emailAddress?.charAt(0)?.toUpperCase() ||
    "?";
  const displayName =
    user?.firstName ||
    user?.primaryEmailAddress?.emailAddress?.split("@")[0] ||
    "Student";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-2 py-1 pr-3 hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20">
        <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display text-sm font-bold">
          {initial}
        </span>
        <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">{displayName}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Signed in as
        </DropdownMenuLabel>
        <div className="px-2 pb-2 text-sm font-medium truncate">
          {user?.primaryEmailAddress?.emailAddress}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setLocation("/my-classes")}>
          <UserIcon className="w-4 h-4 mr-2" /> My classes
        </DropdownMenuItem>
        {isTeacher && (
          <DropdownMenuItem onSelect={() => setLocation("/teacher")}>
            <GraduationCap className="w-4 h-4 mr-2" /> Teacher portal
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void signOut(() => setLocation("/"));
          }}
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Wordmark({ size = "default" }: { size?: "default" | "large" }) {
  const isLarge = size === "large";
  return (
    <span className={`font-display font-bold tracking-tight inline-flex items-baseline gap-1 ${isLarge ? "text-5xl md:text-6xl" : "text-xl"}`}>
      <span className="text-foreground">main</span>
      <span className={`relative text-foreground`}>
        loop
        <span
          aria-hidden="true"
          className={`absolute -right-2 top-0 ${isLarge ? "w-3 h-3 -right-4" : "w-1.5 h-1.5"} rounded-full bg-accent`}
        />
      </span>
    </span>
  );
}

function Marquee() {
  const items = [
    "Now enrolling — Fall '26 cohorts",
    "* * *",
    "Built by working engineers",
    "* * *",
    "Live + self-paced courses",
    "* * *",
    "For ambitious 6th–12th graders",
    "* * *",
    "Python · Vibecoding · AI",
    "* * *",
  ];
  const loop = [...items, ...items];
  return (
    <div
      className="bg-foreground text-background border-y border-foreground overflow-hidden"
      role="marquee"
      aria-label="Site announcements (hover to pause)"
    >
      <div className="flex animate-marquee whitespace-nowrap py-2.5 font-mono text-xs tracking-wider uppercase">
        {loop.map((it, i) => (
          <span key={i} className="px-6 inline-flex items-center gap-3" aria-hidden={i >= items.length}>
            {it.startsWith("*") ? <span className="text-secondary">{it}</span> : it}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu when route changes
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navLinkClass = (active: boolean) =>
    `text-sm font-medium transition-colors relative py-1 ${
      active
        ? "text-foreground after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[3px] after:bg-secondary"
        : "text-muted-foreground hover:text-foreground"
    }`;

  const mobileLinkClass = (active: boolean) =>
    `block py-3 font-display text-2xl font-bold tracking-tight border-b border-border ${
      active ? "text-primary" : "text-foreground"
    }`;

  const navItems: Array<{ href: string; label: string; isActive: (loc: string) => boolean }> = [
    { href: "/courses", label: "Catalog", isActive: (l) => l.startsWith("/courses") },
    { href: "/about", label: "Mission", isActive: (l) => l === "/about" },
    { href: "/for-schools", label: "For Schools", isActive: (l) => l === "/for-schools" },
    { href: "/my-classes", label: "Student Portal", isActive: (l) => l === "/my-classes" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground"
      >
        Skip to main content
      </a>

      {location === "/" && <Marquee />}

      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <Link href="/" className="flex items-center gap-3 transition-opacity hover:opacity-80">
              <Wordmark />
              <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-l border-border pl-3 ml-1">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" aria-hidden="true" />
                est. 2026
              </span>
            </Link>
            <nav className="hidden md:flex gap-7" aria-label="Primary">
              {navItems.map(item => (
                <Link key={item.href} href={item.href} className={navLinkClass(item.isActive(location))}>
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Show when="signed-out">
              <Link
                href="/sign-in"
                className="hidden sm:inline-flex h-9 items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Sign in
              </Link>
              <Link
                href="/sign-up"
                className="hidden sm:inline-flex h-9 items-center justify-center gap-2 rounded-full bg-foreground px-5 py-2 font-mono text-xs uppercase tracking-widest text-background shadow transition-transform hover:-translate-y-0.5"
              >
                Get started <span aria-hidden="true">→</span>
              </Link>
            </Show>
            <Show when="signed-in">
              <UserMenu />
            </Show>
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 rounded-md text-foreground hover:bg-muted transition-colors"
              onClick={() => setMobileOpen(v => !v)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              id="mobile-nav"
              key="mobile-nav"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="md:hidden border-t border-border overflow-hidden bg-background"
              aria-label="Mobile primary"
            >
              <div className="container mx-auto px-4 py-4">
                {navItems.map(item => (
                  <Link key={item.href} href={item.href} className={mobileLinkClass(item.isActive(location))}>
                    {item.label}
                  </Link>
                ))}
                <Show when="signed-out">
                  <Link
                    href="/sign-in"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-border bg-background px-6 py-3 font-mono text-xs uppercase tracking-widest text-foreground w-full"
                  >
                    Sign in
                  </Link>
                  <Link
                    href="/sign-up"
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-6 py-3 font-mono text-xs uppercase tracking-widest text-background w-full"
                  >
                    Get started <span aria-hidden="true">→</span>
                  </Link>
                </Show>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1 outline-none">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="bg-foreground text-background pt-20 pb-10 mt-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-sm opacity-[0.04] pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-6 lg:col-span-7">
              <h2 id="newsletter-heading" className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/50 mb-6">
                // The newsletter
              </h2>
              <p className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6 text-balance">
                Get the <span className="font-italic-serif text-secondary">build log.</span>
                <br />Monthly. No fluff.
              </p>
              <p className="text-background/70 max-w-md mb-6">
                Project ideas, AI workflows, and what our students are shipping. Read in five minutes.
              </p>
              <form
                className="flex flex-col sm:flex-row gap-2 max-w-md"
                onSubmit={(e) => e.preventDefault()}
                aria-labelledby="newsletter-heading"
              >
                <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                <input
                  id="newsletter-email"
                  type="email"
                  placeholder="you@school.edu"
                  className="flex-1 h-12 px-4 rounded-md bg-background/10 border border-background/15 text-background placeholder:text-background/40 focus:outline-none focus:border-secondary"
                />
                <button
                  type="submit"
                  className="h-12 px-6 rounded-md bg-secondary text-secondary-foreground font-bold text-sm uppercase tracking-wider hover:bg-secondary/90 transition-colors"
                  disabled
                >
                  Subscribe →
                </button>
              </form>
            </div>

            <div className="md:col-span-2 lg:col-span-2">
              <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/50 mb-5">// Site</h4>
              <ul className="space-y-3 text-sm">
                <li><Link href="/" className="hover:text-secondary transition-colors">Home</Link></li>
                <li><Link href="/courses" className="hover:text-secondary transition-colors">Catalog</Link></li>
                <li><Link href="/about" className="hover:text-secondary transition-colors">Mission</Link></li>
                <li><Link href="/for-schools" className="hover:text-secondary transition-colors">For Schools</Link></li>
                <li><Link href="/my-classes" className="hover:text-secondary transition-colors">Student Portal</Link></li>
                <li><Link href="/teacher" className="hover:text-secondary transition-colors">Teacher Portal</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2 lg:col-span-2">
              <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/50 mb-5">// Topics</h4>
              <ul className="space-y-3 text-sm">
                <li><Link href="/courses?topic=python" className="hover:text-secondary transition-colors">Python →</Link></li>
                <li><Link href="/courses?topic=vibecoding" className="hover:text-secondary transition-colors">Vibecoding →</Link></li>
                <li><Link href="/courses?topic=ai" className="hover:text-secondary transition-colors">AI Foundations →</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2 lg:col-span-1">
              <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/50 mb-5">// Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link href="/terms" className="hover:text-secondary transition-colors">Terms</Link></li>
                <li><Link href="/privacy" className="hover:text-secondary transition-colors">Privacy</Link></li>
                <li><Link href="/cookies" className="hover:text-secondary transition-colors">Cookies</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-background/15 pt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <Wordmark size="large" />
              <div className="mt-4 font-mono text-xs text-background/50 max-w-sm leading-relaxed">
                The coding bootcamp built for curious teenagers.
                <br />
                <Link href="/privacy" className="hover:text-secondary transition-colors">Privacy</Link>
                <span className="mx-1.5">·</span>
                <Link href="/terms" className="hover:text-secondary transition-colors">Terms</Link>
                <span className="mx-1.5">·</span>
                <Link href="/cookies" className="hover:text-secondary transition-colors">Cookies</Link>
              </div>
            </div>
            <div className="font-mono text-xs text-background/50 text-right">
              <div>&copy; {new Date().getFullYear()} MainLoop Education</div>
              <div className="mt-1 inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" aria-hidden="true" />
                System OK / All cohorts running
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
